import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useMemo, useRef, useState} from 'react'
import {NegativeFeedbackButton, PositiveFeedbackButton} from './Buttons'
import {FeedbackDetailsDialog} from './FeedbackDetailsDialog'
import {useHandleFeedbackSubmit} from './hooks'
import type {FeedbackOptions, FeedbackSubmitHandler, FeedbackType, PostSubmitFeedbackDetailsConfig} from './types'
import {resolveFeedbackPath} from './utils'

export interface CopilotCodeReviewFeedbackProps {
  commentId: string
  commentUrl?: string
  feedbackPath?: string
  feedbackOptions?: FeedbackOptions | null
  additionalParameters?: Record<string, string>
  showNegativeFeedbackModal?: boolean
  postSubmitFeedbackDetails?: PostSubmitFeedbackDetailsConfig
  groupLabel?: string
}

const Feedback: React.FC<CopilotCodeReviewFeedbackProps> = ({
  commentId,
  commentUrl,
  feedbackPath,
  feedbackOptions,
  additionalParameters,
  showNegativeFeedbackModal = true,
  postSubmitFeedbackDetails,
  groupLabel,
}) => {
  const [typeSubmitted, setTypeSubmitted] = useState<FeedbackType>()
  const [followupFeedbackType, setFollowupFeedbackType] = useState<FeedbackType>()
  const feedbackControlsRef = useRef<HTMLDivElement>(null)

  const path = useMemo(() => resolveFeedbackPath({feedbackPath, commentUrl}), [commentUrl, feedbackPath])

  const handleSubmit = useHandleFeedbackSubmit({path, additionalParameters})

  const handleClick: FeedbackSubmitHandler = props => {
    setTypeSubmitted(props.type)
    handleSubmit(props)

    if (postSubmitFeedbackDetails?.feedbackTypes.includes(props.type)) {
      setFollowupFeedbackType(props.type)
    }
  }

  const handleDetailsSubmit: FeedbackSubmitHandler = props => {
    handleSubmit(props)
    setFollowupFeedbackType(undefined)
  }

  const detailsFeedbackOptions =
    postSubmitFeedbackDetails?.feedbackOptions === undefined
      ? feedbackOptions
      : postSubmitFeedbackDetails.feedbackOptions

  return (
    <div
      ref={feedbackControlsRef}
      data-testid={`copilot-code-review-feedback-${commentId}`}
      className="d-flex gap-1"
      role={groupLabel ? 'group' : undefined}
      aria-label={groupLabel}
      tabIndex={postSubmitFeedbackDetails ? -1 : undefined}
    >
      {postSubmitFeedbackDetails && followupFeedbackType && (
        <FeedbackDetailsDialog
          commentId={commentId}
          feedbackType={followupFeedbackType}
          detailsOnly={postSubmitFeedbackDetails.detailsOnly}
          feedbackOptions={detailsFeedbackOptions}
          feedbackChoiceLabel={postSubmitFeedbackDetails.feedbackChoiceLabel}
          title={postSubmitFeedbackDetails.title}
          subtitle={postSubmitFeedbackDetails.subtitle}
          textResponseLabel={postSubmitFeedbackDetails.textResponseLabel}
          allowTextOnly={postSubmitFeedbackDetails.allowTextOnly}
          returnFocusRef={feedbackControlsRef}
          onClose={() => setFollowupFeedbackType(undefined)}
          onSubmit={handleDetailsSubmit}
        />
      )}
      {typeSubmitted !== 'NEGATIVE' && (
        <PositiveFeedbackButton disabled={!!typeSubmitted} commentId={commentId} onClick={handleClick} />
      )}
      {typeSubmitted !== 'POSITIVE' && (
        <NegativeFeedbackButton
          disabled={!!typeSubmitted}
          onClick={handleClick}
          commentId={commentId}
          feedbackOptions={feedbackOptions}
          showNegativeFeedbackModal={showNegativeFeedbackModal}
        />
      )}
    </div>
  )
}

export const CopilotCodeReviewFeedback: React.FC<CopilotCodeReviewFeedbackProps> = props => (
  <ErrorBoundary boundaryName="CopilotCodeReviewFeedback" fallback={null}>
    <Feedback {...props} />
  </ErrorBoundary>
)
